<template>
  <div>
    <div class="d-flex justify-content-between align-items-start mb-3">
      <div>
        <h5 class="mb-1">RSVP via AutoResponder (WhatsApp)</h5>
        <p class="text-muted mb-0" style="max-width:680px">
          Envie um template com botões de resposta rápida e deixe a plataforma mudar o status do convidado
          automaticamente quando ele clica. Cada regra liga um <strong>botão de um template</strong> a um
          <strong>status</strong> (qualquer um) e a uma <strong>resposta automática</strong>. Os acompanhantes acompanham o titular.
        </p>
      </div>
      <button class="btn btn-primary" v-if="enabled" @click="openRule()">
        <i class="mdi mdi-plus"></i> Nova regra
      </button>
    </div>

    <div class="custom-control custom-switch mb-4">
      <input type="checkbox" class="custom-control-input" id="rsvp_enabled" v-model="enabled" @change="saveMaster">
      <label class="custom-control-label" for="rsvp_enabled">Ativar AutoResponder de RSVP</label>
    </div>

    <div class="alert alert-primary" style="font-size:13px">
      <i class="mdi mdi-sitemap"></i>
      Para <strong>fluxos com várias etapas</strong> (clique → confirma/pendente/declina → envia outro
      template → espera novo clique), monte uma <strong>Jornada</strong>: comece com o gatilho
      <em>"Campanha WhatsApp Enviada"</em> e use o passo <em>"WhatsApp: Esperar Clique"</em>.
      <router-link :to="{ name: 'journeys' }" class="alert-link">Abrir Jornadas</router-link>.
      As regras abaixo cobrem apenas o caso simples de um clique → um status.
    </div>

    <div v-if="enabled">
      <div class="alert alert-info" style="font-size:13px">
        <i class="mdi mdi-information-outline"></i>
        Só aparecem templates que possuem botões de <strong>resposta rápida</strong>. O casamento é feito pelo
        <strong>botão</strong> escolhido; quando possível, também pelo template de origem da resposta.
      </div>

      <p v-if="!rules.length" class="text-muted">Nenhuma regra cadastrada. Clique em <strong>Nova regra</strong>.</p>
      <table v-else class="table align-middle">
        <thead>
          <tr>
            <th>Template</th>
            <th>Botão</th>
            <th>Status aplicado</th>
            <th>Resposta automática</th>
            <th style="width:90px">Ativa</th>
            <th style="width:110px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rules" :key="r.id">
            <td>{{ r.template_name || 'Qualquer' }}</td>
            <td><span class="badge badge-soft-primary">{{ r.button_label }}</span></td>
            <td>{{ r.status_label }}<small v-if="r.status === 2 && r.ref_status" class="text-muted"> · {{ refName(r.ref_status) }}</small></td>
            <td><small class="text-muted">{{ r.reply_message ? truncate(r.reply_message) : '—' }}</small></td>
            <td><span class="badge" :class="r.active ? 'badge-success' : 'badge-secondary'" role="button" @click="toggle(r)">{{ r.active ? 'Sim' : 'Não' }}</span></td>
            <td class="text-right">
              <button class="btn btn-sm btn-outline-primary" @click="openRule(r)"><i class="mdi mdi-pencil"></i></button>
              <button class="btn btn-sm btn-outline-danger" @click="remove(r)"><i class="mdi mdi-trash-can-outline"></i></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal de regra -->
    <b-modal v-model="modal" :title="form.id ? 'Editar regra' : 'Nova regra'" size="lg" hide-footer>
      <form @submit.prevent="save()">
        <div class="row">
          <div class="form-group col-sm-6">
            <label>Template *</label>
            <select class="custom-select" v-model="form.wa_template_id" @change="onTemplate">
              <option :value="null" disabled>Selecione…</option>
              <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.namespace }}</option>
            </select>
            <small v-if="!templates.length" class="text-danger">Nenhum template com botão de resposta rápida encontrado.</small>
          </div>
          <div class="form-group col-sm-6">
            <label>Botão *</label>
            <select class="custom-select" v-model="form.button_label" :disabled="!buttonOptions.length" @change="onButton">
              <option value="" disabled>Selecione…</option>
              <option v-for="b in buttonOptions" :key="b.index" :value="b.text">{{ b.text }}</option>
            </select>
          </div>
        </div>
        <div class="row">
          <div class="form-group" :class="form.status === 2 ? 'col-sm-6' : 'col-sm-12'">
            <label>Status a aplicar *</label>
            <select class="custom-select" v-model.number="form.status">
              <option v-for="s in statusSelect" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div class="form-group col-sm-6" v-if="form.status === 2">
            <label>Tipo de pendência</label>
            <select class="custom-select" v-model.number="form.ref_status">
              <option v-for="(name, i) in refstatusList" :key="i" :value="Number(i)">{{ name }}</option>
            </select>
          </div>
        </div>
        <div class="form-group">
          <label>Resposta automática</label>
          <textarea class="form-control" rows="3" v-model="form.reply_message" placeholder="Ex: Presença confirmada! Nos vemos no evento. 🎉"></textarea>
          <small class="text-muted">Deixe em branco para não responder nada após o clique.</small>
        </div>
        <div class="custom-control custom-switch mb-3">
          <input type="checkbox" class="custom-control-input" id="rule_active" v-model="form.active">
          <label class="custom-control-label" for="rule_active">Regra ativa</label>
        </div>
        <div class="text-right">
          <button type="button" class="btn btn-light mr-2" @click="modal = false">Cancelar</button>
          <button type="submit" class="btn btn-primary" :disabled="saving">Salvar</button>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import services from './services'

const emptyRule = () => ({ id: null, wa_template_id: null, button_label: '', button_index: null, status: 3, ref_status: null, reply_message: '', active: true })

export default {
  name: 'wa-rsvp-autoresponder',
  data () {
    return {
      enabled: false,
      rules: [],
      templates: [],
      modal: false,
      saving: false,
      form: emptyRule()
    }
  },
  computed: {
    ...mapState('wa', { settingWA: 'settingWA' }),
    ...mapState('settings', ['statusSelect', 'refstatusList']),
    buttonOptions () {
      const t = this.templates.find(x => x.id === this.form.wa_template_id)
      return t ? t.buttons : []
    }
  },
  watch: {
    settingWA: {
      immediate: true,
      handler (val) { if (val && val.id) this.enabled = !!val.rsvp_button_enabled }
    }
  },
  mounted () {
    if (!this.settingWA || !this.settingWA.id) this.getSettingWA()
    this.GetStatus()
    this.GetRefStatus()
    this.loadRules()
    this.loadTemplates()
  },
  methods: {
    ...mapActions('wa', { getSettingWA: 'GetSettingWA', saveWA: 'saveWA' }),
    ...mapActions('settings', { GetStatus: 'GetStatusSelect', GetRefStatus: 'GetRefStatusList' }),
    truncate (s) { return s.length > 60 ? s.slice(0, 60) + '…' : s },
    refName (i) { return this.refstatusList ? this.refstatusList[i] : '' },
    loadRules () { services.listRules().then(r => { this.rules = r.data.data }).catch(() => {}) },
    loadTemplates () { services.listRuleTemplates().then(r => { this.templates = r.data.data }).catch(() => {}) },
    async saveMaster () {
      if (!this.settingWA || !this.settingWA.id) {
        errorMessage('Configure o WhatsApp antes de ativar o AutoResponder.', 3000)
        this.enabled = false
        return
      }
      try {
        // Payload mínimo: só o campo deste toggle. Reenviar o objeto inteiro
        // do settingWA arrasta campos derivados/ausentes (ex.: colunas de outra
        // migration não aplicada) e quebra o UPDATE.
        await this.saveWA({ id: this.settingWA.id, rsvp_button_enabled: this.enabled ? 1 : 0 })
        successMessage(this.enabled ? 'AutoResponder ativado.' : 'AutoResponder desativado.', 1500)
      } catch (e) { errorMessage('Falha ao salvar.', 3000) }
    },
    onTemplate () { this.form.button_label = ''; this.form.button_index = null },
    onButton () {
      const b = this.buttonOptions.find(x => x.text === this.form.button_label)
      this.form.button_index = b ? b.index : null
    },
    openRule (r) {
      this.form = r
        ? { id: r.id, wa_template_id: r.wa_template_id, button_label: r.button_label, button_index: r.button_index, status: r.status, ref_status: r.ref_status, reply_message: r.reply_message || '', active: !!r.active }
        : emptyRule()
      this.modal = true
    },
    async save () {
      if (!this.form.wa_template_id || !this.form.button_label || !this.form.status) {
        errorMessage('Selecione template, botão e status.', 3000)
        return
      }
      this.saving = true
      try {
        const payload = {
          wa_template_id: this.form.wa_template_id,
          button_label: this.form.button_label,
          button_index: this.form.button_index,
          status: this.form.status,
          ref_status: this.form.status === 2 ? this.form.ref_status : null,
          reply_message: this.form.reply_message || null,
          active: this.form.active ? 1 : 0
        }
        if (this.form.id) await services.updateRule(this.form.id, payload)
        else await services.createRule(payload)
        this.modal = false
        successMessage('Regra salva.', 2000)
        this.loadRules()
      } catch (e) { errorMessage('Falha ao salvar regra.', 3000) } finally { this.saving = false }
    },
    async toggle (r) { try { await services.toggleRule(r.id); r.active = !r.active } catch (e) { errorMessage('Falha ao alterar status.', 3000) } },
    async remove (r) {
      const ok = await this.$bvModal.msgBoxConfirm(`Remover a regra do botão "${r.button_label}"?`, { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
      if (!ok) return
      try { await services.deleteRule(r.id); this.loadRules() } catch (e) { errorMessage('Falha ao remover.', 3000) }
    }
  }
}
</script>
